import { HIGHLIGHT_COLORS } from 'containers/Tiptap/components/tiptap-ui/color-highlight-button/use-color-highlight';

export type Rgb = [number, number, number];

function hslToRgb(h: number, s: number, l: number): Rgb {
  const sat = s / 100;
  const lum = l / 100;
  const k = (n: number): number => (n + h / 30) % 12;
  const a = sat * Math.min(lum, 1 - lum);
  const f = (n: number): number => lum - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
}

const toHex = (rgb: Rgb): string => `#${rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;

export enum PaletteColor {
  Gray = 'gray',
  Brown = 'brown',
  Orange = 'orange',
  Yellow = 'yellow',
  Green = 'green',
  Blue = 'blue',
  Purple = 'purple',
  Pink = 'pink',
  Red = 'red',
}

// Light-theme values from styles/_variables.scss — a DOCX has no theme, and it prints on white.
const TEXT_COLOR_HSL: Record<PaletteColor, [number, number, number]> = {
  [PaletteColor.Gray]: [45, 2, 46],
  [PaletteColor.Brown]: [19, 31, 47],
  [PaletteColor.Orange]: [30, 89, 45],
  [PaletteColor.Yellow]: [38, 62, 49],
  [PaletteColor.Green]: [148, 32, 39],
  [PaletteColor.Blue]: [202, 54, 43],
  [PaletteColor.Purple]: [274, 32, 54],
  [PaletteColor.Pink]: [328, 49, 53],
  [PaletteColor.Red]: [2, 62, 55],
};

export const TEXT_COLOR_PALETTE: ReadonlyArray<{ name: PaletteColor; rgb: Rgb }> = Object.values(PaletteColor).map(
  (name) => ({ name, rgb: hslToRgb(...TEXT_COLOR_HSL[name]) }),
);

const DEFAULT_COLOR_VARS = new Set(['var(--tt-color-text)', 'var(--tt-bg-color)']);

// Tiptap's DOCX exporter can't parse CSS variables; `null` means the default swatch.
export function resolvePaletteColor(value: string): string | null {
  const trimmed = value.trim();
  if (DEFAULT_COLOR_VARS.has(trimmed)) return null;

  const textName = /^var\(--tt-color-text-([a-z]+)\)$/.exec(trimmed)?.[1];
  const swatch = TEXT_COLOR_PALETTE.find((entry) => entry.name === textName);
  if (swatch) return toHex(swatch.rgb);

  return HIGHLIGHT_COLORS.find((color) => color.value === trimmed)?.colorValue ?? value;
}
